import { useState } from 'react'
import { Activity, Cpu, HardDrive, Gauge, Users, AlertTriangle } from 'lucide-react'
import { useMetricsData, type TimePeriod } from './useMetricsData'
import { MetricKPICard } from './MetricKPICard'
import { RequestRateChart } from './RequestRateChart'
import { ResponseTimeChart } from './ResponseTimeChart'
import { HealthStatusTracker } from './HealthStatusTracker'
import { MetricsTable } from './MetricsTable'

interface MetricsDashboardProps {
  appId: string
}

export function MetricsDashboard({ appId }: MetricsDashboardProps) {
  const [period, setPeriod] = useState<TimePeriod>('24h')
  const { timeSeries, summaries, availableMetrics, loading, error } = useMetricsData(appId, period)

  // Determine which KPI cards to show based on available metrics
  const hasRequests = availableMetrics.includes('http_requests_total')
  const hasErrors = availableMetrics.includes('http_errors_total')
  const hasResponseTime = availableMetrics.includes('http_request_duration_p50')
  const hasCpu = availableMetrics.includes('cpu_usage_percent')
  const hasMemory =
    availableMetrics.includes('memory_usage_percent') ||
    availableMetrics.includes('memory_usage_bytes')
  const hasConnections = availableMetrics.includes('active_connections')

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4">
        <div className="flex items-center gap-2 text-red-700">
          <AlertTriangle className="w-5 h-5" />
          <span className="text-sm">Failed to load metrics: {error}</span>
        </div>
      </div>
    )
  }

  // Show empty state if no metrics at all
  if (!loading && availableMetrics.length === 0) {
    return (
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-8 text-center">
        <Activity className="w-10 h-10 text-slate-300 mx-auto mb-3" />
        <h3 className="text-sm font-medium text-slate-900 mb-1">No Metrics Data</h3>
        <p className="text-sm text-slate-500">
          Configure a metrics endpoint in your app settings to start collecting data.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header with time period selector */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">Infrastructure Metrics</h2>
        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
          {(['1h', '6h', '24h', '7d'] as const).map(p => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                period === p
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      {/* KPI Cards Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {hasRequests && (
          <MetricKPICard
            title="Requests"
            metricName="http_requests_total"
            summary={summaries['http_requests_total']}
            timeSeries={timeSeries}
            format="number"
            icon={<Activity className="w-full h-full" />}
            color="blue"
          />
        )}
        {hasErrors && (
          <MetricKPICard
            title="Errors"
            metricName="http_errors_total"
            summary={summaries['http_errors_total']}
            timeSeries={timeSeries}
            format="number"
            icon={<AlertTriangle className="w-full h-full" />}
            color="red"
            invertTrend
          />
        )}
        {hasResponseTime && (
          <MetricKPICard
            title="p50 Latency"
            metricName="http_request_duration_p50"
            summary={summaries['http_request_duration_p50']}
            timeSeries={timeSeries}
            format="ms"
            icon={<Gauge className="w-full h-full" />}
            color="green"
            invertTrend
          />
        )}
        {hasCpu && (
          <MetricKPICard
            title="CPU Usage"
            metricName="cpu_usage_percent"
            summary={summaries['cpu_usage_percent']}
            timeSeries={timeSeries}
            format="percent"
            icon={<Cpu className="w-full h-full" />}
            color="purple"
            invertTrend
          />
        )}
        {hasMemory && (
          <MetricKPICard
            title="Memory"
            metricName={
              availableMetrics.includes('memory_usage_percent')
                ? 'memory_usage_percent'
                : 'memory_usage_bytes'
            }
            summary={
              summaries[
                availableMetrics.includes('memory_usage_percent')
                  ? 'memory_usage_percent'
                  : 'memory_usage_bytes'
              ]
            }
            timeSeries={timeSeries}
            format={availableMetrics.includes('memory_usage_percent') ? 'percent' : 'bytes'}
            icon={<HardDrive className="w-full h-full" />}
            color="amber"
            invertTrend
          />
        )}
        {hasConnections && (
          <MetricKPICard
            title="Connections"
            metricName="active_connections"
            summary={summaries['active_connections']}
            timeSeries={timeSeries}
            format="number"
            icon={<Users className="w-full h-full" />}
            color="blue"
          />
        )}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {hasRequests && (
          <RequestRateChart timeSeries={timeSeries} period={period} loading={loading} />
        )}
        {hasResponseTime && (
          <ResponseTimeChart timeSeries={timeSeries} period={period} loading={loading} />
        )}
      </div>

      {/* Health Status Tracker */}
      <HealthStatusTracker timeSeries={timeSeries} loading={loading} />

      {/* All Metrics Table */}
      <MetricsTable
        timeSeries={timeSeries}
        summaries={summaries}
        availableMetrics={availableMetrics}
        loading={loading}
      />
    </div>
  )
}
